// NotificationSlider.tsx
import React, { useState, useEffect } from 'react';
import { FiX, FiTrash2, FiBell, FiCheckSquare, FiSquare, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { RiDraggable } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../app/store';
import {
  fetchNotifications,
  fetchUnreadCount,
  deleteNotification,
  markNotificationAsRead,
  markNotificationsAsBulkRead,
  markAllNotificationsAsRead,
  bulkDeleteNotifications
} from '../features/notificationSlider/notificationsSlice';
import { AppDispatch } from '../app/store';
import { toast, ToastContainer } from 'react-toastify';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';

interface NotificationSliderProps {
  isOpen: boolean;
  onClose: () => void;
}

const RiDraggableComponent = RiDraggable as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiTrash2Component = FiTrash2 as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiBellComponent = FiBell as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiCheckSquareComponent = FiCheckSquare as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiSquareComponent = FiSquare as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiCheckCircleComponent = FiCheckCircle as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiAlertTriangleComponent = FiAlertTriangle as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const NotificationSlider: React.FC<NotificationSliderProps> = ({
  isOpen,
  onClose,
}) => {
  const notifications = useSelector(
    (state: RootState) => state.notifications.items
  );
  const unreadCount = notifications.filter(n => !n.read).length;

  const dispatch = useDispatch<AppDispatch>();
  const [showDelete, setShowDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isMarkingRead, setIsMarkingRead] = useState<string | null>(null);
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [isBulkMarking, setIsBulkMarking] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Refresh notifications when slider opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchNotifications());
      dispatch(fetchUnreadCount());
      // Clear selections when slider opens
      setSelectedNotifications(new Set());
    }
  }, [isOpen, dispatch]);

  // Clear selections when slider closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedNotifications(new Set());
      setShowDeleteModal(false);
    }
  }, [isOpen]);

  const toggleDelete = (id: string) => {
    setShowDelete(showDelete === id ? null : id);
  };

  const handleDelete = async (id: string, title: string) => {
    try {
      setIsDeleting(id);
      await dispatch(deleteNotification(id)).unwrap();

      toast.success(`Notification "${title}" removed`, {
        position: 'bottom-right',
        autoClose: 3000,
      });

      // Remove from selection if deleted
      setSelectedNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });

      setShowDelete(null);
    } catch (error) {
      toast.error('Failed to delete notification', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      // console.error('Failed to delete notification:', error);
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = async (notification: any) => {
    // If notification is unread, mark it as read
    if (!notification.read) {
      try {
        setIsMarkingRead(notification.notificationId);
        await dispatch(markNotificationAsRead(notification.notificationId)).unwrap();
      } catch (error) {
        // console.error('Failed to mark notification as read:', error);
        toast.error('Failed to mark notification as read')
      } finally {
        setIsMarkingRead(null);
      }
    }

    // Navigate to the deeplink if provided (commented out for now)
    // if (notification.deeplink) {
    //   onClose();
    //   window.location.href = notification.deeplink;
    // }
  };

  // Toggle selection of a notification
  const toggleSelection = (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Handle bulk mark as read
  const handleBulkMarkAsRead = async () => {
    if (selectedNotifications.size === 0) return;

    try {
      setIsBulkMarking(true);
      const notificationIds = Array.from(selectedNotifications);
      await dispatch(markNotificationsAsBulkRead(notificationIds)).unwrap();

      toast.success(`${selectedNotifications.size} notification(s) marked as read`, {
        position: 'bottom-right',
        autoClose: 3000,
      });

      // Clear selections after successful bulk operation
      setSelectedNotifications(new Set());

      // Refresh notifications to get updated state
      await dispatch(fetchNotifications());
      await dispatch(fetchUnreadCount());
    } catch (error) {
      toast.error('Failed to mark notifications as read', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      // console.error('Failed to mark notifications as read:', error);
    } finally {
      setIsBulkMarking(false);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) {
      toast.info('All notifications are already read', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsMarkingAll(true);
      await dispatch(markAllNotificationsAsRead()).unwrap();

      toast.success('All notifications marked as read', {
        position: 'bottom-right',
        autoClose: 3000,
      });

      // Clear selections after successful operation
      setSelectedNotifications(new Set());

      // Refresh notifications to get updated state
      await dispatch(fetchNotifications());
      await dispatch(fetchUnreadCount());
    } catch (error) {
      toast.error('Failed to mark all notifications as read', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      // console.error('Failed to mark all notifications as read:', error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  // Handle bulk delete - show modal
  const handleBulkDelete = () => {
    if (selectedNotifications.size === 0) return;
    setShowDeleteModal(true);
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      setShowDeleteModal(false);
      const notificationIds = Array.from(selectedNotifications);
      await dispatch(bulkDeleteNotifications(notificationIds)).unwrap();

      toast.success(`${selectedNotifications.size} notification(s) deleted`, {
        position: 'bottom-right',
        autoClose: 3000,
      });

      // Clear selections after successful bulk operation
      setSelectedNotifications(new Set());

      // Refresh notifications to get updated state
      await dispatch(fetchNotifications());
      await dispatch(fetchUnreadCount());
    } catch (error) {
      toast.error('Failed to delete notifications', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      // console.error('Failed to delete notifications:', error);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Cancel bulk delete
  const cancelBulkDelete = () => {
    setShowDeleteModal(false);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Check if notification is recent (within last 24 hours)
  const isRecent = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    return diffHours < 24;
  };

  // Check if any notification is selected
  const hasSelectedNotifications = selectedNotifications.size > 0;

  // Empty state
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <FiBellComponent size={48} className="text-gray-300 mb-4" />
      <h3 className="text-lg font-medium text-gray-600">No notifications</h3>
      <p className="text-sm text-gray-400 mt-1">You're all caught up!</p>
    </div>
  );

  // Render notification item
  const renderNotificationItem = (notification: any) => {
    const isSelected = selectedNotifications.has(notification.notificationId);
    const isUnread = !notification.read;

    return (
      <div key={notification.notificationId} className='relative overflow-hidden'>
        <div
          className={`flex relative items-center bg-[#F5F8FA] px-3 py-2 rounded-lg transition-transform duration-300 z-10 cursor-pointer hover:bg-[#EDF2F7] ${showDelete === notification.notificationId ? '-translate-x-16' : 'translate-x-0'
            } ${isUnread ? 'border-l-4 border-blue-500' : ''}`}
          onClick={() => handleNotificationClick(notification)}
        >
          {/* Checkbox */}
          <div
            className="mr-3 flex-shrink-0"
            onClick={(e) => toggleSelection(notification.notificationId, e)}
          >
            {isSelected ? (
              <FiCheckSquareComponent size={20} className="text-blue-500" />
            ) : (
              <FiSquareComponent size={20} className="text-gray-400 hover:text-gray-600" />
            )}
          </div>

          <div className='flex-1 min-w-0'>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h4 className='font-semibold text-[16px] text-[#1F2937] truncate'>
                  {notification.title}
                </h4>
                {isUnread && (
                  <span className="text-xs text-blue-500 whitespace-nowrap shrink-0">
                    • Unread
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                {formatDate(notification.createdAt)}
              </span>
            </div>
            <p className='text-[14px] text-[#657584] line-clamp-2'>
              {notification.message}
            </p>

            {isMarkingRead === notification.notificationId && (
              <div className="text-xs text-gray-400 mt-1">
                Marking as read...
              </div>
            )}
          </div>
          <button
            className='p-2 text-gray-500 hover:text-gray-700 z-20 relative'
            onClick={(e) => {
              e.stopPropagation();
              toggleDelete(notification.notificationId);
            }}
            disabled={isDeleting === notification.notificationId}
          >
            {isDeleting === notification.notificationId ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
            ) : (
              <RiDraggableComponent />
            )}
          </button>
        </div>

        {showDelete === notification.notificationId && (
          <button
            className='absolute top-0 right-0 bottom-0 z-0 flex items-center justify-end bg-red-100 text-red-600 pl-10 pr-5 rounded-lg hover:bg-red-200 transition-colors'
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(notification.notificationId, notification.title);
            }}
            disabled={isDeleting === notification.notificationId}
          >
            <FiTrash2Component size={18} />
          </button>
        )}
      </div>
    );
  };

  // Split notifications into recent (last 24 hours) and older
  const recentNotifications = notifications.filter(n => isRecent(n.createdAt));
  const olderNotifications = notifications.filter(n => !isRecent(n.createdAt));

  // Delete Confirmation Modal
  const DeleteConfirmationModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl w-96 max-w-md p-6 animate-scale-in">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-red-100 rounded-full p-3">
              <FiAlertTriangleComponent size={28} className="text-red-600" />
            </div>
          </div>

          <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
            Delete Notifications
          </h3>

          <p className="text-sm text-gray-600 text-center mb-6">
            Are you sure you want to delete <span className="font-semibold text-gray-900">{selectedNotifications.size}</span> notification{selectedNotifications.size > 1 ? 's' : ''}?
            This action cannot be undone.
          </p>

          <div className="flex space-x-3">
            <button
              onClick={cancelBulkDelete}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={confirmBulkDelete}
              disabled={isBulkDeleting}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBulkDeleting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </div>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`fixed top-0 right-0 h-full w-96 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className='flex items-center justify-between p-4 border-b'>
          <div className="flex items-center space-x-2">
            <h2 className='text-[18px] font-semibold text-[#3F5265]'>
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1">
            {/* Mark All as Read Button */}
            <div className="relative group">
              <button
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0 || isMarkingAll}
                className={`flex items-center px-2 py-1 rounded transition-colors ${isMarkingAll
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : unreadCount === 0
                    ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    : 'bg-green-50 text-green-500 hover:bg-green-100'
                  }`}
              >
                {isMarkingAll ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                ) : (
                  <FiCheckCircleComponent size={18} />
                )}
              </button>

              {/* Tailwind Tooltip */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                {isMarkingAll
                  ? 'Marking all as read...'
                  : unreadCount === 0
                    ? 'All notifications are already read'
                    : `Mark all notifications as read`}
              </div>
            </div>

            {/* Bulk Delete Button - Always visible, disabled when no selection */}
            <div className="relative group">
              <button
                onClick={handleBulkDelete}
                disabled={!hasSelectedNotifications || isBulkDeleting}
                className={`flex items-center space-x-1 px-2 py-1 rounded transition-colors ${isBulkDeleting
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : !hasSelectedNotifications
                    ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    : 'bg-red-50 text-red-500 hover:bg-red-100'
                  }`}
              >
                {isBulkDeleting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                ) : (
                  <FiTrash2Component size={18} />
                )}
                <span className="text-sm font-medium">
                  {isBulkDeleting ? '' : `(${selectedNotifications.size})`}
                </span>
              </button>

              {/* Tailwind Tooltip */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                {isBulkDeleting
                  ? 'Deleting selected notifications...'
                  : !hasSelectedNotifications
                    ? 'Select notifications to delete'
                    : `Delete ${selectedNotifications.size} notification(s)`}
              </div>
            </div>

            {/* Bulk Mark Read Button - Always visible, disabled when no selection */}
            <div className="relative group">
              <button
                onClick={handleBulkMarkAsRead}
                disabled={!hasSelectedNotifications || isBulkMarking}
                className={`flex items-center space-x-1 px-2 py-1 rounded transition-colors ${isBulkMarking
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : !hasSelectedNotifications
                    ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    : 'bg-blue-50 text-blue-500 hover:bg-blue-100'
                  }`}
              >
                {isBulkMarking ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                ) : (
                  <FiCheckSquareComponent size={18} />
                )}
                <span className="text-sm font-medium">
                  {isBulkMarking ? '' : `(${selectedNotifications.size})`}
                </span>
              </button>

              {/* Tailwind Tooltip */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                {isBulkMarking
                  ? 'Marking selected as read...'
                  : !hasSelectedNotifications
                    ? 'Select notifications to mark as read'
                    : `Mark ${selectedNotifications.size} notification(s) as read`}
              </div>
            </div>

            <button
              onClick={onClose}
              className='p-1 rounded hover:bg-gray-100 text-[#3F5265]'
            >
              <FiXComponent size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='p-4 overflow-y-auto h-[calc(100vh-80px)]'>
          {notifications.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className='space-y-5'>
              {/* Recent Section (last 24 hours) */}
              {recentNotifications.length > 0 && (
                <div>
                  <h3 className='text-[14px] font-medium text-[#9FA9B2] mb-2'>
                    Recent
                  </h3>
                  <div className='space-y-3'>
                    {recentNotifications.map(renderNotificationItem)}
                  </div>
                </div>
              )}

              {/* Older Section */}
              {olderNotifications.length > 0 && (
                <div>
                  <h3 className='text-[14px] font-medium text-[#9FA9B2] mb-2'>
                    Older
                  </h3>
                  <div className='space-y-3'>
                    {olderNotifications.map(renderNotificationItem)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ToastContainer position='bottom-right' autoClose={3000} />
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal />
    </>
  );
};

export default NotificationSlider;